import { Component, createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { MathJaxBaseContext } from "better-react-mathjax";

const FallbackMathJaxContext = createContext(null);

/**
 * Cache of already-typeset HTML (SVG). Avoids re-typesetting the same truncated
 * header when the grid remounts on subject / rating-type switches.
 */
const typesetCache = new Map();

class MathTypesetBoundary extends Component {
    static getDerivedStateFromError() {
        return { failed: true };
    }

    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    componentDidCatch() {
        // Swallow MathJax typeset races — fall back to raw HTML.
    }

    render() {
        if (this.state.failed) {
            return (
                <span
                    className={this.props.className}
                    dangerouslySetInnerHTML={{ __html: this.props.fallbackHtml || "" }}
                />
            );
        }
        return this.props.children;
    }
}

MathTypesetBoundary.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    fallbackHtml: PropTypes.string
};

/**
 * Mounts a host node, runs MathJax.typesetPromise, then swaps to cached SVG HTML.
 * Cleanup calls typesetClear while the node still exists (useLayoutEffect) so
 * subject switches do not leave MathJax walking a detached tree (nextSibling crash).
 */
const TypesettingHost = ({ html, cacheKey, className, as: Tag }) => {
    const mjContext = useContext(MathJaxBaseContext || FallbackMathJaxContext);
    const hostRef = useRef(null);
    const [doneHtml, setDoneHtml] = useState(null);

    useLayoutEffect(() => {
        let cancelled = false;

        if (!mjContext || html == null || html === "") {
            return undefined;
        }

        const node = hostRef.current;
        if (!node) {
            return undefined;
        }

        mjContext.promise
            .then((MathJax) =>
                MathJax.startup.promise.then(() => {
                    if (cancelled || !hostRef.current) return undefined;
                    MathJax.typesetClear([node]);
                    return MathJax.typesetPromise([node]).then(() => {
                        if (cancelled || !hostRef.current) return;
                        const out = hostRef.current.innerHTML;
                        typesetCache.set(cacheKey, out);
                        // Detach MathJax from this node before React swaps to static SVG.
                        try {
                            MathJax.typesetClear([node]);
                        } catch {
                            // ignore
                        }
                        setDoneHtml(out);
                    });
                })
            )
            .catch(() => {
                // Leave raw HTML; boundary covers thrown cases.
            });

        return () => {
            cancelled = true;
            try {
                const mj = typeof window !== "undefined" ? window.MathJax : null;
                if (mj?.typesetClear) {
                    mj.typesetClear([node]);
                }
            } catch {
                // ignore
            }
        };
    }, [html, cacheKey, mjContext]);

    if (doneHtml) {
        return <Tag className={className} dangerouslySetInnerHTML={{ __html: doneHtml }} />;
    }

    // No MathJax provider (tests / edge cases) — show raw HTML.
    if (!mjContext) {
        return <Tag className={className} dangerouslySetInnerHTML={{ __html: html || "" }} />;
    }

    return (
        <Tag
            className={className}
            ref={hostRef}
            style={{ visibility: "hidden" }}
            dangerouslySetInnerHTML={{ __html: html || "" }}
        />
    );
};

TypesettingHost.propTypes = {
    html: PropTypes.string,
    cacheKey: PropTypes.string,
    className: PropTypes.string,
    as: PropTypes.elementType
};

/**
 * Typesets HTML that may contain TeX ($...$, fractions, etc.) for rating-grid
 * headers / selected labels. Prefer this over bare <MathJax> in lists that remount.
 */
const SafeMathHtml = ({ html, cacheKey, className, as: Tag = "span" }) => {
    const key = cacheKey || html || "";
    const cached = typesetCache.get(key);

    if (cached) {
        return <Tag className={className} dangerouslySetInnerHTML={{ __html: cached }} />;
    }

    return (
        <MathTypesetBoundary fallbackHtml={html} className={className}>
            <TypesettingHost key={key} html={html} cacheKey={key} className={className} as={Tag} />
        </MathTypesetBoundary>
    );
};

SafeMathHtml.propTypes = {
    html: PropTypes.string,
    cacheKey: PropTypes.string,
    className: PropTypes.string,
    as: PropTypes.elementType
};

export default SafeMathHtml;
